import { z } from 'zod'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import { optionalProjectId } from '../../common/optionalFields'

export const RagTextSchema = z
  .object({
    docId: z.number(),
    text: z.string(),
    createdOnTsMs: z.number(),
    createdById: z.number(),
    createdByFirstName: z.string(),
    createdBySurname: z.string(),
    updatedAtTsMs: z.number(),
    updatedById: z.number(),
    updatedByFirstName: z.string(),
    updatedBySurname: z.string()
  })
  .describe('RagTextSchema')

export type RagText = z.infer<typeof RagTextSchema>

export const GetRagTextResponseSchema = z
  .object({
    items: z.array(RagTextSchema),
    pageCount: z.number(),
    documentCount: z.number().optional()
  })
  .describe('GetRagTextResponseSchema')

export type GetRagTextResponse = z.infer<typeof GetRagTextResponseSchema>

export const AddRagTextSchema = z.object({
  text: z.string().min(1, 'Text is required')
})

export type AddRagText = z.infer<typeof AddRagTextSchema>

// Mirrors SearchRequestV2 in backend/internal/api/http/rag_content_management.go
export const FetchRagTextsSchema = z.object({
  docId: z.number().optional(),
  searchText: z.string().optional(),
  tagIds: z.array(z.number()).optional(),
  userIds: z.array(z.number()).optional(),
  currentPage: z.number(),
  projectId: z.number().optional()
})

export type FetchRagTexts = z.infer<typeof FetchRagTextsSchema>

const defaultFetchParams: FetchRagTexts = {
  currentPage: 1
}

export async function fetchRagTexts(params: Partial<FetchRagTexts> = {}): Promise<GetRagTextResponse> {
  const body: FetchRagTexts = { ...defaultFetchParams, ...params }
  const res = await fetch('/api/content/text/searchv2', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data = await res.json()
  return parseDataOfType(data, GetRagTextResponseSchema)
}

export async function createRagText(text: string, projectId?: number): Promise<void> {
  const res = await fetch('/api/content/text', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ text, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

export async function updateRagText(docId: number, text: string, projectId?: number): Promise<void> {
  const res = await fetch(`/api/content/text/${docId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ text, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

export async function deleteRagText(docId: number, projectId?: number): Promise<void> {
  const url =
    projectId !== undefined ? `/api/content/text/${docId}?projectId=${projectId}` : `/api/content/text/${docId}`
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}
